import { CardContainer } from "@msys/ui";
import {
  Alert,
  Button,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik } from "formik";
import React from "react";
import { FormikClearErrorsOnChange } from "../../../../commons/FormikClearErrorsOnChange.js";
import { TextField } from "../../../../commons/form-fields/TextField.js";
import { useUnsavedChangesGuard } from "../../../../commons/hooks/useUnsavedChangesGuard.js";
import type { IntegrationSettingsProps } from "../../integrationRegistry.js";
import { getIntegrationMeta } from "../../integrationRegistry.js";
import {
  CardSaveRow,
  FormDirtyTracker,
  useCardDirtyRegistry,
} from "./integrationCardForm.js";
import { SettingsPageLayout } from "./SettingsPageLayout.js";
import { useIntegrationSettingsBase } from "./useIntegrationSettingsBase.js";

// API endpoint pairs grouped for readability
const API_ENDPOINT_GROUPS = [
  {
    title: "Sales Quotation Services",
    endpoints: [
      ["apiSalesQuotationSrvUrl", "apiSalesQuotationSrvBasePath"],
      ["apiYm1SalesQuotationSrvUrl", "apiYm1SalesQuotationSrvBasePath"],
      ["apiYapiSalesQuotation001SrvUrl", "apiYapiSalesQuotation001SrvBasePath"],
      [
        "apiSalesQuotationYcrSdM1AgCopyUrl",
        "apiSalesQuotationYcrSdM1AgCopyBasePath",
      ],
    ],
  },
  {
    title: "Value Help Services",
    endpoints: [
      ["apiValueHelpCloudReadyV4Url", "apiValueHelpCloudReadyV4BasePath"],
      ["apiValueHelpOnPremiseV4Url", "apiValueHelpOnPremiseV4BasePath"],
    ],
  },
  {
    title: "Product & Inventory Services",
    endpoints: [
      ["apiMdCProductMaintainSrvUrl", "apiMdCProductMaintainSrvBasePath"],
      ["apiMdCustomerMasterSrv01Url", "apiMdCustomerMasterSrv01BasePath"],
      ["apiYmmUiV4KmatMaterialUrl", "apiYmmUiV4KmatMaterialBasePath"],
      ["apiYmmUiV4KmatListungUrl", "apiYmmUiV4KmatListungBasePath"],
      ["apiYmmProductShortTexts04Url", "apiYmmProductShortTexts04BasePath"],
    ],
  },
  {
    title: "Pricing & Conditions",
    endpoints: [
      [
        "apiSlsPricingConditionRecordSrvUrl",
        "apiSlsPricingConditionRecordSrvBasePath",
      ],
      [
        "apiPurgPrcgConditionRecordSrvUrl",
        "apiPurgPrcgConditionRecordSrvBasePath",
      ],
      ["apiYsdSubmitCondCalcSrvUrl", "apiYsdSubmitCondCalcSrvBasePath"],
      ["apiInfoRecordSrvUrl", "apiInfoRecordSrvBasePath"],
    ],
  },
  {
    title: "Business Partners & Documents",
    endpoints: [
      ["apiBusinessPartnerSrvUrl", "apiBusinessPartnerSrvBasePath"],
      ["apiDocumentManagementSrvUrl", "apiDocumentManagementSrvBasePath"],
      ["apiDocumentFlowUrl"],
      ["apiCustomerCreateFisMpmUrl"],
      ["apiSalesQuotationCrmRedirectUrl"],
    ],
  },
];

// Settings paths whose form-field name differs from the schema path (the
// flat form renames nested keys); everything else maps by its first path
// segment
const SERVER_PATH_TO_FIELD: Record<string, string> = {
  "crm.userIdcustomFieldKey": "crmUserIdCustomFieldKey",
  "crm.defaultJobTitle": "crmDefaultJobTitle",
  "crm.defaultCashPlanningGroup": "crmDefaultCashPlanningGroup",
  "gdprEmailConfig.fromEmailAddess": "gdprFromEmailAddress",
  "gdprEmailConfig.emailTemplateId": "gdprEmailTemplateId",
  "salesQuotationItemPositionSettings.positionStepSize": "positionStepSize",
  "salesQuotationItemPositionSettings.roundDistributedPositionsTo":
    "roundDistributedPositionsTo",
  "salesQuotationItemPositionSettings.firstPosition": "firstPosition",
  "salesQuotationItemPositionSettings.minPosition": "minPosition",
  "salesQuotationItemPositionSettings.maxPosition": "maxPosition",
};

// A settings-validation failure surfaces as a serialized issue array. Only
// the paths are read (to highlight fields) — the raw text, which echoes the
// received values, is never rendered.
function serverValidationPaths(message: string): string[] | null {
  try {
    const parsed: unknown = JSON.parse(message);
    if (!Array.isArray(parsed)) return null;
    const paths: string[] = [];
    for (const issue of parsed) {
      if (issue && typeof issue === "object") {
        const path = (issue as Record<string, unknown>).path;
        if (Array.isArray(path) && path.length > 0) {
          paths.push(path.map(String).join("."));
        }
      }
    }
    return paths.length > 0 ? paths : null;
  } catch {
    return null;
  }
}

const SIMPLE_STRING_FIELDS = [
  "sapPartnerId",
  "salesQuotationType",
  "distributionChannel",
  "organizationDivision",
  "importFromSalesQuotationType",
  "salesQuotationInitialStatus",
  "salesQuotationStatusField",
  "salesQuotationCreatedByUser",
  "defaultProductSupplierId",
  "salesQuotationItemPlantDefault",
  "salesQuotationItemSectionItemCategory",
  "salesQuotationItemSectionItemBeginItemMaterial",
  "salesQuotationItemSectionItemEndItemMaterial",
  "salesQuotationItemComponentCategory",
  "fioriBusinessPartnerBasePath",
  "kmatListingAssortment",
  "clientCertificateSecretId",
  "salesQuotationQuoteIdCustomFieldKey",
] as const;

// Per-card form slices: every section card is its own Formik form with its
// own Save/Cancel row. A card's save overlays only that card's slice on the
// baseline derived from the stored settings, so other cards' unsaved edits
// are never swept along while their last saved values are preserved.
interface GeneralValues {
  sapPartnerId: string;
  salesQuotationType: string;
  importFromSalesQuotationType: string;
  defaultProductSupplierId: string;
  salesQuotationCreatedByUser: string;
  salesQuotationQuoteIdCustomFieldKey: string;
  hideResetPassword: boolean;
  hideDeleteProfile: boolean;
  disableCraftsmenAccountingFields: boolean;
}

interface OrganisationValues {
  availableSalesOrganizations: string;
  distributionChannel: string;
  organizationDivision: string;
  fioriBusinessPartnerBasePath: string;
  clientCertificateSecretId: string;
}

interface QuotesValues {
  salesQuotationStatuses: string;
  salesQuotationInitialStatus: string;
  salesQuotationStatusField: string;
  salesQuotationSdDocumentReasons: string;
  salesQuotationItemSalesDocumentRejectionReasonDefault: string;
  salesQuotationItemSalesDocumentRejectionReasons: string;
  salesQuotationHandleDeletedItemsAsRejected: boolean;
  salesQuotationDummyMaterialNumbers: string;
}

interface ItemsValues {
  positionStepSize: number | string;
  roundDistributedPositionsTo: number | string;
  firstPosition: number | string;
  minPosition: number | string;
  maxPosition: number | string;
  salesQuotationItemSectionItemCategory: string;
  salesQuotationItemComponentCategory: string;
  salesQuotationItemHeaderCategories: string;
  salesQuotationItemSectionItemBeginItemMaterial: string;
  salesQuotationItemSectionItemEndItemMaterial: string;
  salesQuotationItemPlantDefault: string;
  itemCategoryOverrides: string;
}

interface ProductsValues {
  configurableProductSupplierCatalogueIds: string;
  kmatListingAssortment: string;
  bpFilterRolesAll: string;
  bpFilterRolesExclude: string;
  fisSolrUrl: string;
}

interface CrmGdprValues {
  crmUserIdCustomFieldKey: string;
  crmDefaultJobTitle: string;
  crmDefaultCashPlanningGroup: string;
  gdprFromEmailAddress: string;
  gdprEmailTemplateId: string;
}

type NamedFormValues = GeneralValues &
  OrganisationValues &
  QuotesValues &
  ItemsValues &
  ProductsValues &
  CrmGdprValues;

type ApiEndpointsValues = Record<string, string>;

type SetFieldError = (field: string, message: string | undefined) => void;

export const SapS4HanaSettings = ({
  integrationId,
  mode,
}: IntegrationSettingsProps) => {
  const { t } = useTranslate("Integrations");
  const meta = getIntegrationMeta(integrationId);
  const {
    orgIntegration,
    refetchDashboard,
    loading,
    removeLoading,
    handleAdd,
    handleUpdate,
    handleDisconnect,
    enqueueSnackbar,
  } = useIntegrationSettingsBase(integrationId, mode);

  const integrationName = meta.sidebarLabel ?? "SAP S/4HANA";
  const settings = (orgIntegration?.settings as any) ?? {};

  const isCreate = mode === "create";
  const [connecting, setConnecting] = React.useState(false);

  const { anyDirty, dirtyOf } = useCardDirtyRegistry();
  useUnsavedChangesGuard(
    anyDirty,
    t("You have unsaved changes. Leave the page anyway?")
  );

  // Deep-link connect page: nothing is required to connect — the plugin
  // creates the integration with safe defaults. The normal path is the
  // connect modal on the overview; this page only backs stale links.
  if (isCreate) {
    return (
      <SettingsPageLayout
        integrationId={integrationId}
        integrationName={integrationName}
        title={`${t("Connect")} SAP S/4HANA`}
      >
        <CardContainer title={t("Connection")}>
          <Stack spacing={2} sx={{ px: 1, pt: 0.5, pb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t("All settings can be configured after connecting.")}
            </Typography>
            <Stack direction="row" sx={{ justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                disabled={connecting}
                onClick={async () => {
                  setConnecting(true);
                  try {
                    await handleAdd();
                  } catch (e) {
                    enqueueSnackbar(
                      t(
                        "Settings could not be saved. Please check your input."
                      ),
                      { variant: "error" }
                    );
                    setConnecting(false);
                  }
                }}
              >
                {t("Connect")}
              </Button>
            </Stack>
          </Stack>
        </CardContainer>
      </SettingsPageLayout>
    );
  }

  // Baseline flat form values derived from the currently stored settings —
  // the same derivation the per-card initial values use. Each card's save
  // overlays its own slice on this baseline, so a save never sweeps other
  // cards' unsaved edits but never drops their saved values either.
  const generalInitial: GeneralValues = {
    sapPartnerId: settings.sapPartnerId ?? "",
    salesQuotationType: settings.salesQuotationType ?? "",
    importFromSalesQuotationType: settings.importFromSalesQuotationType ?? "",
    defaultProductSupplierId: settings.defaultProductSupplierId ?? "",
    salesQuotationCreatedByUser: settings.salesQuotationCreatedByUser ?? "",
    salesQuotationQuoteIdCustomFieldKey:
      settings.salesQuotationQuoteIdCustomFieldKey ?? "",
    hideResetPassword: settings.hideResetPassword ?? false,
    hideDeleteProfile: settings.hideDeleteProfile ?? false,
    disableCraftsmenAccountingFields:
      settings.disableCraftsmenAccountingFields ?? false,
  };

  const organisationInitial: OrganisationValues = {
    availableSalesOrganizations: (
      settings.availableSalesOrganizations ?? []
    ).join(", "),
    distributionChannel: settings.distributionChannel ?? "",
    organizationDivision: settings.organizationDivision ?? "",
    fioriBusinessPartnerBasePath: settings.fioriBusinessPartnerBasePath ?? "",
    clientCertificateSecretId: settings.clientCertificateSecretId ?? "",
  };

  const quotesInitial: QuotesValues = {
    salesQuotationStatuses: (settings.salesQuotationStatuses ?? []).join(", "),
    salesQuotationInitialStatus: settings.salesQuotationInitialStatus ?? "",
    salesQuotationStatusField: settings.salesQuotationStatusField ?? "",
    salesQuotationSdDocumentReasons: (
      settings.salesQuotationSdDocumentReasons ?? []
    ).join(", "),
    salesQuotationItemSalesDocumentRejectionReasonDefault:
      settings.salesQuotationItemSalesDocumentRejectionReasonDefault ?? "",
    salesQuotationItemSalesDocumentRejectionReasons: (
      settings.salesQuotationItemSalesDocumentRejectionReasons ?? []
    ).join(", "),
    salesQuotationHandleDeletedItemsAsRejected:
      settings.salesQuotationHandleDeletedItemsAsRejected ?? false,
    salesQuotationDummyMaterialNumbers: (
      settings.salesQuotationDummyMaterialNumbers ?? []
    ).join(", "),
  };

  const itemsInitial: ItemsValues = {
    positionStepSize:
      settings.salesQuotationItemPositionSettings?.positionStepSize ?? 10,
    roundDistributedPositionsTo:
      settings.salesQuotationItemPositionSettings
        ?.roundDistributedPositionsTo ?? 10,
    firstPosition:
      settings.salesQuotationItemPositionSettings?.firstPosition ?? 1,
    minPosition: settings.salesQuotationItemPositionSettings?.minPosition ?? 1,
    maxPosition: settings.salesQuotationItemPositionSettings?.maxPosition ?? "",
    salesQuotationItemSectionItemCategory:
      settings.salesQuotationItemSectionItemCategory ?? "",
    salesQuotationItemComponentCategory:
      settings.salesQuotationItemComponentCategory ?? "",
    salesQuotationItemHeaderCategories: (
      settings.salesQuotationItemHeaderCategories ?? []
    ).join(", "),
    salesQuotationItemSectionItemBeginItemMaterial:
      settings.salesQuotationItemSectionItemBeginItemMaterial ?? "",
    salesQuotationItemSectionItemEndItemMaterial:
      settings.salesQuotationItemSectionItemEndItemMaterial ?? "",
    salesQuotationItemPlantDefault:
      settings.salesQuotationItemPlantDefault ?? "",
    itemCategoryOverrides: (settings.itemCategoryOverrides ?? []).join(", "),
  };

  const productsInitial: ProductsValues = {
    configurableProductSupplierCatalogueIds: (
      settings.configurableProductSupplierCatalogueIds ?? []
    ).join(", "),
    kmatListingAssortment: settings.kmatListingAssortment ?? "",
    bpFilterRolesAll: (
      Object.values(
        (settings.salesQuotationBusinessPartnerFilterBy as any) ?? {}
      ) as any[]
    )
      .flatMap((v: any) => v?.businessPartnerRolesAll ?? [])
      .filter((v: string, i: number, a: string[]) => a.indexOf(v) === i)
      .join(", "),
    bpFilterRolesExclude: (
      Object.values(
        (settings.salesQuotationBusinessPartnerFilterBy as any) ?? {}
      ) as any[]
    )
      .flatMap((v: any) => v?.businessPartnerRolesExclude ?? [])
      .filter((v: string, i: number, a: string[]) => a.indexOf(v) === i)
      .join(", "),
    fisSolrUrl: settings.fisSolrUrl ?? "",
  };

  const crmGdprInitial: CrmGdprValues = {
    crmUserIdCustomFieldKey: settings.crm?.userIdcustomFieldKey ?? "",
    crmDefaultJobTitle: settings.crm?.defaultJobTitle ?? "",
    crmDefaultCashPlanningGroup: settings.crm?.defaultCashPlanningGroup ?? "",
    gdprFromEmailAddress: settings.gdprEmailConfig?.fromEmailAddess ?? "",
    gdprEmailTemplateId: settings.gdprEmailConfig?.emailTemplateId ?? "",
  };

  const apiEndpointsInitial: ApiEndpointsValues = {};
  for (const group of API_ENDPOINT_GROUPS) {
    for (const pair of group.endpoints) {
      for (const key of pair) {
        apiEndpointsInitial[key] = settings[key] ?? "";
      }
    }
  }

  const namedBaseline: NamedFormValues = {
    ...generalInitial,
    ...organisationInitial,
    ...quotesInitial,
    ...itemsInitial,
    ...productsInitial,
    ...crmGdprInitial,
  };

  const buildUpdatedSettings = (
    values: NamedFormValues,
    endpointValues: ApiEndpointsValues
  ) => {
    const commaToArray = (s: string) =>
      s
        .split(",")
        .map(v => v.trim())
        .filter(Boolean);

    const updated: any = { ...settings };
    for (const key of SIMPLE_STRING_FIELDS) {
      updated[key] = values[key];
    }
    for (const group of API_ENDPOINT_GROUPS) {
      for (const pair of group.endpoints) {
        for (const key of pair) {
          updated[key] = endpointValues[key];
        }
      }
    }
    updated.availableSalesOrganizations = commaToArray(
      values.availableSalesOrganizations
    );
    updated.salesQuotationStatuses = commaToArray(
      values.salesQuotationStatuses
    );
    updated.salesQuotationSdDocumentReasons = commaToArray(
      values.salesQuotationSdDocumentReasons
    );
    updated.salesQuotationItemSalesDocumentRejectionReasons = commaToArray(
      values.salesQuotationItemSalesDocumentRejectionReasons
    );
    updated.salesQuotationItemSalesDocumentRejectionReasonDefault =
      values.salesQuotationItemSalesDocumentRejectionReasonDefault;
    updated.salesQuotationHandleDeletedItemsAsRejected =
      values.salesQuotationHandleDeletedItemsAsRejected;
    updated.salesQuotationItemHeaderCategories = commaToArray(
      values.salesQuotationItemHeaderCategories
    );
    updated.salesQuotationItemPositionSettings = {
      positionStepSize: Number(values.positionStepSize),
      roundDistributedPositionsTo: Number(values.roundDistributedPositionsTo),
      firstPosition: Number(values.firstPosition),
      minPosition: Number(values.minPosition),
      maxPosition: values.maxPosition ? Number(values.maxPosition) : null,
    };
    // Preserve existing partner filter structure, just update the role arrays
    updated.salesQuotationBusinessPartnerFilterBy =
      settings.salesQuotationBusinessPartnerFilterBy ?? {};
    updated.configurableProductSupplierCatalogueIds = commaToArray(
      values.configurableProductSupplierCatalogueIds
    );
    updated.salesQuotationDummyMaterialNumbers = commaToArray(
      values.salesQuotationDummyMaterialNumbers
    );
    updated.itemCategoryOverrides = commaToArray(values.itemCategoryOverrides);
    updated.fisSolrUrl = values.fisSolrUrl || undefined;
    updated.crm = {
      userIdcustomFieldKey: values.crmUserIdCustomFieldKey,
      defaultJobTitle: values.crmDefaultJobTitle || undefined,
      defaultCashPlanningGroup: values.crmDefaultCashPlanningGroup || undefined,
    };
    updated.gdprEmailConfig = {
      fromEmailAddess: values.gdprFromEmailAddress,
      emailTemplateId: values.gdprEmailTemplateId,
    };
    updated.disableCraftsmenAccountingFields =
      values.disableCraftsmenAccountingFields;
    updated.hideResetPassword = values.hideResetPassword;
    updated.hideDeleteProfile = values.hideDeleteProfile;

    return updated;
  };

  const saveCard = async (
    buildPayload: () => unknown,
    cardValues: object,
    setFieldError: SetFieldError
  ) => {
    try {
      await handleUpdate(buildPayload());
      // handleUpdate does not refetch — the awaited refetch delivers the new
      // stored settings so enableReinitialize resets the saved card clean
      await refetchDashboard();
    } catch (e) {
      // Never surface the raw validation error (it echoes received
      // values) — highlight the offending fields instead. Only fields
      // belonging to the submitting card can be highlighted; anything
      // else falls back to the generic snackbar.
      const message = e instanceof Error ? e.message : String(e);
      const paths = serverValidationPaths(message);
      let highlighted = 0;
      for (const path of paths ?? []) {
        const field = SERVER_PATH_TO_FIELD[path] ?? path.split(".")[0];
        if (Object.prototype.hasOwnProperty.call(cardValues, field)) {
          setFieldError(field, t("Invalid value"));
          highlighted++;
        }
      }
      enqueueSnackbar(
        highlighted > 0
          ? t("Please check the highlighted fields.")
          : t("Settings could not be saved. Please check your input."),
        { variant: "error" }
      );
    }
  };

  const submitNamedCard = (
    values: Partial<NamedFormValues>,
    setFieldError: SetFieldError
  ) =>
    saveCard(
      () =>
        buildUpdatedSettings(
          { ...namedBaseline, ...values },
          apiEndpointsInitial
        ),
      values,
      setFieldError
    );

  const submitEndpointsCard = (
    values: ApiEndpointsValues,
    setFieldError: SetFieldError
  ) =>
    saveCard(
      () =>
        buildUpdatedSettings(namedBaseline, {
          ...apiEndpointsInitial,
          ...values,
        }),
      values,
      setFieldError
    );

  return (
    <SettingsPageLayout
      integrationId={integrationId}
      integrationName={integrationName}
      title={`${integrationName} — ${t("Settings")}`}
      onDisconnect={handleDisconnect}
      events={orgIntegration?.events ?? []}
      onRefreshEvents={refetchDashboard}
      hasDashboard={!!orgIntegration?.health}
      removeLoading={removeLoading}
      loading={loading && !orgIntegration}
    >
      {/* Flowing section cards instead of tabs (2026-07-14 review). Each
          card is an independent form with its own Save/Cancel row. */}
      <Stack spacing={2}>
        <Formik<GeneralValues>
          initialValues={generalInitial}
          onSubmit={(values, { setFieldError }) =>
            submitNamedCard(values, setFieldError)
          }
          enableReinitialize
        >
          {fp => (
            <Form>
              <FormikClearErrorsOnChange />
              <FormDirtyTracker
                dirty={fp.dirty}
                onDirtyChange={dirtyOf("general")}
              />
              <CardContainer title={t("General")}>
                <Stack spacing={1} sx={{ px: 1, pt: 0.5, pb: 1 }}>
                  <TextField
                    name="sapPartnerId"
                    label="sapPartnerId"
                    size="small"
                  />
                  <TextField
                    name="salesQuotationType"
                    label="salesQuotationType"
                    size="small"
                  />
                  <TextField
                    name="importFromSalesQuotationType"
                    label="importFromSalesQuotationType"
                    size="small"
                  />
                  <TextField
                    name="defaultProductSupplierId"
                    label="defaultProductSupplierId"
                    size="small"
                  />
                  <TextField
                    name="salesQuotationCreatedByUser"
                    label="salesQuotationCreatedByUser"
                    size="small"
                  />
                  <TextField
                    name="salesQuotationQuoteIdCustomFieldKey"
                    label="salesQuotationQuoteIdCustomFieldKey"
                    size="small"
                  />

                  <Typography variant="subtitle2" sx={{ pt: 1 }}>
                    {t("UI Toggles")}
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={fp.values.hideResetPassword}
                        onChange={e =>
                          fp.setFieldValue(
                            "hideResetPassword",
                            e.target.checked
                          )
                        }
                      />
                    }
                    label={t("Hide reset password button")}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={fp.values.hideDeleteProfile}
                        onChange={e =>
                          fp.setFieldValue(
                            "hideDeleteProfile",
                            e.target.checked
                          )
                        }
                      />
                    }
                    label={t("Hide delete profile button")}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={fp.values.disableCraftsmenAccountingFields}
                        onChange={e =>
                          fp.setFieldValue(
                            "disableCraftsmenAccountingFields",
                            e.target.checked
                          )
                        }
                      />
                    }
                    label={t("Disable craftsmen accounting fields")}
                  />
                  <CardSaveRow fp={fp} />
                </Stack>
              </CardContainer>
            </Form>
          )}
        </Formik>

        <Formik<OrganisationValues>
          initialValues={organisationInitial}
          onSubmit={(values, { setFieldError }) =>
            submitNamedCard(values, setFieldError)
          }
          enableReinitialize
        >
          {fp => (
            <Form>
              <FormikClearErrorsOnChange />
              <FormDirtyTracker
                dirty={fp.dirty}
                onDirtyChange={dirtyOf("organisation")}
              />
              <CardContainer title={t("Organisation")}>
                <Stack spacing={1} sx={{ px: 1, pt: 0.5, pb: 1 }}>
                  <TextField
                    name="availableSalesOrganizations"
                    label={t("Available Sales Organizations (comma-separated)")}
                    size="small"
                  />
                  <TextField
                    name="distributionChannel"
                    label="distributionChannel"
                    size="small"
                  />
                  <TextField
                    name="organizationDivision"
                    label="organizationDivision"
                    size="small"
                  />
                  <TextField
                    name="fioriBusinessPartnerBasePath"
                    label="fioriBusinessPartnerBasePath"
                    size="small"
                  />
                  <TextField
                    name="clientCertificateSecretId"
                    label="clientCertificateSecretId"
                    size="small"
                  />
                  <CardSaveRow fp={fp} />
                </Stack>
              </CardContainer>
            </Form>
          )}
        </Formik>

        <Formik<QuotesValues>
          initialValues={quotesInitial}
          onSubmit={(values, { setFieldError }) =>
            submitNamedCard(values, setFieldError)
          }
          enableReinitialize
        >
          {fp => (
            <Form>
              <FormikClearErrorsOnChange />
              <FormDirtyTracker
                dirty={fp.dirty}
                onDirtyChange={dirtyOf("quotes")}
              />
              <CardContainer title={t("Quotes")}>
                <Stack spacing={1} sx={{ px: 1, pt: 0.5, pb: 1 }}>
                  <TextField
                    name="salesQuotationStatuses"
                    label={t("Quotation Statuses (comma-separated)")}
                    size="small"
                  />
                  <TextField
                    name="salesQuotationInitialStatus"
                    label="salesQuotationInitialStatus"
                    size="small"
                  />
                  <TextField
                    name="salesQuotationStatusField"
                    label="salesQuotationStatusField"
                    size="small"
                  />
                  <TextField
                    name="salesQuotationSdDocumentReasons"
                    label={t("SD Document Reasons (comma-separated)")}
                    size="small"
                  />
                  <TextField
                    name="salesQuotationItemSalesDocumentRejectionReasonDefault"
                    label={t("Default Rejection Reason")}
                    size="small"
                  />
                  <TextField
                    name="salesQuotationItemSalesDocumentRejectionReasons"
                    label={t("Item Rejection Reasons (comma-separated)")}
                    size="small"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={
                          fp.values.salesQuotationHandleDeletedItemsAsRejected
                        }
                        onChange={e =>
                          fp.setFieldValue(
                            "salesQuotationHandleDeletedItemsAsRejected",
                            e.target.checked
                          )
                        }
                      />
                    }
                    label={t("Handle deleted items as rejected")}
                  />
                  <TextField
                    name="salesQuotationDummyMaterialNumbers"
                    label={t("Dummy Material Numbers (comma-separated)")}
                    size="small"
                  />
                  <CardSaveRow fp={fp} />
                </Stack>
              </CardContainer>
            </Form>
          )}
        </Formik>

        <Formik<ItemsValues>
          initialValues={itemsInitial}
          onSubmit={(values, { setFieldError }) =>
            submitNamedCard(values, setFieldError)
          }
          enableReinitialize
        >
          {fp => (
            <Form>
              <FormikClearErrorsOnChange />
              <FormDirtyTracker
                dirty={fp.dirty}
                onDirtyChange={dirtyOf("items")}
              />
              <CardContainer title={t("Items")}>
                <Stack spacing={1} sx={{ px: 1, pt: 0.5, pb: 1 }}>
                  <Typography variant="subtitle2">
                    {t("Position settings")}
                  </Typography>
                  <TextField
                    name="positionStepSize"
                    label={t("Position Step Size")}
                    type="number"
                    size="small"
                  />
                  <TextField
                    name="roundDistributedPositionsTo"
                    label={t("Round Distributed Positions To")}
                    type="number"
                    size="small"
                  />
                  <TextField
                    name="firstPosition"
                    label={t("First Position")}
                    type="number"
                    size="small"
                  />
                  <TextField
                    name="minPosition"
                    label={t("Min Position")}
                    type="number"
                    size="small"
                  />
                  <TextField
                    name="maxPosition"
                    label={t("Max Position (empty = no limit)")}
                    type="number"
                    size="small"
                  />

                  <Typography variant="subtitle2" sx={{ pt: 1 }}>
                    {t("Item categories")}
                  </Typography>
                  <TextField
                    name="salesQuotationItemSectionItemCategory"
                    label="salesQuotationItemSectionItemCategory"
                    size="small"
                  />
                  <TextField
                    name="salesQuotationItemComponentCategory"
                    label="salesQuotationItemComponentCategory"
                    size="small"
                  />
                  <TextField
                    name="salesQuotationItemHeaderCategories"
                    label={t("Header Categories (comma-separated)")}
                    size="small"
                  />
                  <TextField
                    name="salesQuotationItemSectionItemBeginItemMaterial"
                    label="salesQuotationItemSectionItemBeginItemMaterial"
                    size="small"
                  />
                  <TextField
                    name="salesQuotationItemSectionItemEndItemMaterial"
                    label="salesQuotationItemSectionItemEndItemMaterial"
                    size="small"
                  />
                  <TextField
                    name="salesQuotationItemPlantDefault"
                    label="salesQuotationItemPlantDefault"
                    size="small"
                  />
                  <TextField
                    name="itemCategoryOverrides"
                    label={t("Item Category Overrides (comma-separated)")}
                    size="small"
                  />
                  <CardSaveRow fp={fp} />
                </Stack>
              </CardContainer>
            </Form>
          )}
        </Formik>

        <Formik<ProductsValues>
          initialValues={productsInitial}
          onSubmit={(values, { setFieldError }) =>
            submitNamedCard(values, setFieldError)
          }
          enableReinitialize
        >
          {fp => (
            <Form>
              <FormikClearErrorsOnChange />
              <FormDirtyTracker
                dirty={fp.dirty}
                onDirtyChange={dirtyOf("products")}
              />
              <CardContainer title={t("Products")}>
                <Stack spacing={1} sx={{ px: 1, pt: 0.5, pb: 1 }}>
                  <TextField
                    name="configurableProductSupplierCatalogueIds"
                    label={t(
                      "Configurable Product Catalogue IDs (comma-separated)"
                    )}
                    size="small"
                  />
                  <TextField
                    name="kmatListingAssortment"
                    label="kmatListingAssortment"
                    size="small"
                  />
                  <TextField
                    name="bpFilterRolesAll"
                    label={t("Required Roles (comma-separated)")}
                    size="small"
                  />
                  <TextField
                    name="bpFilterRolesExclude"
                    label={t("Excluded Roles (comma-separated)")}
                    size="small"
                  />
                  <TextField
                    name="fisSolrUrl"
                    label={t("FIS SOLR URL (optional)")}
                    size="small"
                  />
                  <CardSaveRow fp={fp} />
                </Stack>
              </CardContainer>
            </Form>
          )}
        </Formik>

        <Formik<CrmGdprValues>
          initialValues={crmGdprInitial}
          onSubmit={(values, { setFieldError }) =>
            submitNamedCard(values, setFieldError)
          }
          enableReinitialize
        >
          {fp => (
            <Form>
              <FormikClearErrorsOnChange />
              <FormDirtyTracker
                dirty={fp.dirty}
                onDirtyChange={dirtyOf("crmGdpr")}
              />
              <CardContainer title={t("CRM / GDPR")}>
                <Stack spacing={1} sx={{ px: 1, pt: 0.5, pb: 1 }}>
                  <Typography variant="subtitle2">{t("CRM")}</Typography>
                  <TextField
                    name="crmUserIdCustomFieldKey"
                    label={t("User ID Custom Field Key")}
                    size="small"
                  />
                  <TextField
                    name="crmDefaultJobTitle"
                    label={t("Default Job Title")}
                    size="small"
                  />
                  <TextField
                    name="crmDefaultCashPlanningGroup"
                    label={t("Default Cash Planning Group")}
                    size="small"
                  />

                  <Typography variant="subtitle2" sx={{ pt: 1 }}>
                    {t("GDPR")}
                  </Typography>
                  <TextField
                    name="gdprFromEmailAddress"
                    label={t("From Email Address")}
                    size="small"
                  />
                  <TextField
                    name="gdprEmailTemplateId"
                    label={t("Email Template ID")}
                    size="small"
                  />
                  <CardSaveRow fp={fp} />
                </Stack>
              </CardContainer>
            </Form>
          )}
        </Formik>

        <Formik<ApiEndpointsValues>
          initialValues={apiEndpointsInitial}
          onSubmit={(values, { setFieldError }) =>
            submitEndpointsCard(values, setFieldError)
          }
          enableReinitialize
        >
          {fp => (
            <Form>
              <FormikClearErrorsOnChange />
              <FormDirtyTracker
                dirty={fp.dirty}
                onDirtyChange={dirtyOf("apiEndpoints")}
              />
              <CardContainer
                title={t("API Endpoints")}
                isExpandable
                isInitiallyClosed
              >
                <Stack spacing={2} sx={{ px: 1, pt: 0.5, pb: 1 }}>
                  <Alert severity="info">
                    {t("Only change on support instructions.")}
                  </Alert>
                  {API_ENDPOINT_GROUPS.map(group => (
                    <Stack key={group.title} spacing={1}>
                      <Typography variant="subtitle2">{group.title}</Typography>
                      {group.endpoints.map(pair => (
                        <Stack key={pair[0]} spacing={0.5}>
                          <TextField
                            name={pair[0]}
                            label={pair[0]}
                            size="small"
                            slotProps={{
                              input: { sx: { fontFamily: "monospace" } },
                            }}
                          />
                          {pair[1] && (
                            <TextField
                              name={pair[1]}
                              label={pair[1]}
                              size="small"
                              slotProps={{
                                input: { sx: { fontFamily: "monospace" } },
                              }}
                            />
                          )}
                        </Stack>
                      ))}
                    </Stack>
                  ))}
                  <CardSaveRow fp={fp} />
                </Stack>
              </CardContainer>
            </Form>
          )}
        </Formik>
      </Stack>
    </SettingsPageLayout>
  );
};
